import { InjectionToken, Provider } from '@angular/core';
import { AdditionalQuestionType } from '../labels/fixture-enum-labels';

/**
 * Per-tenant configuration of the prediction game's FEATURE layer — the tab shell, the
 * ranking tab, the groups tab and the profile modal they share.
 *
 * Sibling of `PREDICTION_GAME_TENANT_CONFIG`, and deliberately a separate token: that one
 * is data the `type:data-access` layer consumes (the club, scoring fallbacks, fixture
 * selection), this one is what the `type:feature` components need (which tabs exist, what
 * the groups tab may offer, which extra loads a tenant hangs off the shell).
 *
 * Every field here exists because the two tenants differ in it today. Nothing in the
 * shared feature layer branches on a tenant name — it reads this configuration instead.
 *
 * The hook-shaped fields (`loadExtras`, `isRefreshingExtras`, `onInit`, `canCreateGroup`,
 * `isPending`, `extraInputs`) are plain functions rather than services so a tenant can
 * close over whatever it likes (its own facade, its own translation service) in the app
 * layer, where the concrete implementations live. They are called inside the shared
 * components; `providePredictionGameFeatureConfig` builds the object in an injection
 * context so `inject()` works while assembling them.
 *
 * Those hooks come in two kinds, and the distinction matters — see {@link SignalHook}
 * and {@link ActionHook}. Every hook field below is typed as one of them, so which
 * contract a tenant has to honour is visible at the field rather than only in prose.
 */
export interface PredictionGameTabConfig {
  /** Translation key of the tab label — the shell renders it through `kikkzTranslate`. */
  labelKey: string;
  /** Child route under the prediction-game shell, e.g. `'ranking'`. */
  route: string;
  /** `true` hides the tab in production builds (today: the debug tab). */
  devOnly?: boolean;
}

/**
 * A hook a shared component reads INSIDE a `computed()`.
 *
 * It must therefore derive its result from signals only: a value read from a plain field
 * (or captured in a closure while the configuration was assembled) never invalidates the
 * `computed()`, so the component would keep showing the first value forever. Conversely
 * it must not have side effects — a `computed()` may re-run at any time.
 */
export type SignalHook<T> = () => T;

/**
 * A hook a shared component calls for its side effect, at a defined moment (`ngOnInit`,
 * pull-to-refresh). Nothing reads its result and it never runs inside a `computed()`, so
 * it is free to dispatch events, start requests and read plain state.
 */
export type ActionHook = () => void;

export interface PredictionGameShellConfig {
  /**
   * Extra loads the tenant hangs off the shell — run on init AND on every pull-to-refresh,
   * on top of the fixtures and the scoring config the shell always loads (effzeh: the
   * season prediction and the squad).
   */
  loadExtras?: ActionHook;
  /**
   * Extra in-flight state ORed into the pull-to-refresh indicator, so the spinner stays
   * up while ANY request `loadExtras` started is still running — every one of them, or
   * the indicator disappears while the tab is still filling in (effzeh: the season
   * prediction AND the squad).
   */
  isRefreshingExtras?: SignalHook<boolean>;
}

export interface PredictionGameRankingConfig {
  /**
   * Extra work on ranking-tab init, on top of loading the server's filter options
   * (effzeh: make sure the squad is loaded, because the season answers resolve player
   * ids to names).
   */
  onInit?: ActionHook;
}

export interface PredictionGameGroupsConfig {
  /**
   * Whether the "Gruppe erstellen" form is offered at all. b04 only lets club members
   * create groups (`B04UserFacade.isMember`), effzeh lets everyone.
   */
  canCreateGroup: SignalHook<boolean>;
  /** Whether the overview explains that creating a group requires a membership. */
  requiresMembership: boolean;
  /** Whether the group tables carry the "Ø Pkt" column. */
  showAveragePoints: boolean;
  /** Body copy of the "Tippgruppen" info bottom sheet (one entry = one paragraph). */
  infoParagraphs: readonly string[];
}

export interface PredictionGamePlayerSelectionConfig {
  /** Show only the player's last name in the first-goal-scorer picker. */
  useLastName: boolean;
  /** Placeholder image for players without a photo; `null` keeps the component default. */
  playerFallbackImage: string | null;
}

export interface PredictionGameAdditionalQuestionConfig {
  /** Card headline per question type, shown above the predictor control. */
  titles: Record<AdditionalQuestionType, string>;
  /** Info-sheet body copy per question type (paragraphs separated by a blank line). */
  info: Record<AdditionalQuestionType, string>;
}

export interface PredictionGameUserStatsModalConfig {
  /**
   * Extra in-flight gate: the profile modal opens only once this is `false` as well.
   * effzeh loads the tapped user's season answers alongside the statistics and must not
   * open a half-filled modal (it renders once and never re-renders).
   */
  isPending?: SignalHook<boolean>;
  /**
   * Extra inputs merged into `RankingUserModal` when the profile modal opens — evaluated
   * at open time, so a translated headline picks up the current language.
   *
   * Neither a {@link SignalHook} nor an {@link ActionHook}: it takes an argument and its
   * result is read once, outside any `computed()`, at the moment the modal opens.
   */
  extraInputs?: (isCurrentUser: boolean) => Record<string, unknown>;
}

export interface PredictionGameFeatureConfig {
  /** Tabs of the prediction-game shell, in display order. */
  tabs: readonly PredictionGameTabConfig[];
  shell: PredictionGameShellConfig;
  ranking: PredictionGameRankingConfig;
  groups: PredictionGameGroupsConfig;
  playerSelection: PredictionGamePlayerSelectionConfig;
  /**
   * Headline and info-sheet copy of the bonus question, per question type. Pure tenant
   * wording ("für den FC" vs. "für Bayer"), which is why it is configuration and not a
   * shared constant — the tenants keep their own `ADDITIONAL_QUESTION_TITLES` /
   * `ADDITIONAL_QUESTION_INFO` and hand them in here.
   */
  additionalQuestion: PredictionGameAdditionalQuestionConfig;
  userStatsModal: PredictionGameUserStatsModalConfig;
}

export const PREDICTION_GAME_FEATURE_CONFIG = new InjectionToken<PredictionGameFeatureConfig>(
  'PREDICTION_GAME_FEATURE_CONFIG',
);

/**
 * Provides the tenant's prediction-game feature configuration.
 *
 * Takes a factory rather than a value so the hook fields can `inject()` the tenant's own
 * services while the configuration is assembled — `canCreateGroup` reaching for b04's
 * `AuthFacade`, `loadExtras` for effzeh's `SeasonPredictionFacade`.
 */
export function providePredictionGameFeatureConfig(factory: () => PredictionGameFeatureConfig): Provider {
  return { provide: PREDICTION_GAME_FEATURE_CONFIG, useFactory: factory };
}
